import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { LuCheck, LuTrash2 } from "react-icons/lu";
import { api, ApiError , errMsg } from "../lib/api";
import { useActiveFlat } from "../lib/active-flat";
import { useAuth } from "../lib/auth";
import type { ShoppingItem } from "../lib/types";
import { Avatar } from "../components/Avatar";
import { Button, CenteredSpinner, EmptyState, ErrorBanner, IconButton, Input } from "../components/ui";
import { formatRelativeTime } from "../lib/format";

export function ShoppingPage() {
  const { flat } = useActiveFlat();
  const { user } = useAuth();
  const flatId = flat!.id;

  const [items, setItems] = useState<ShoppingItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState("");

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await api.get<ShoppingItem[]>(`/shopping/by-flat/${flatId}`);
      setItems(data);
    } catch (err) {
      setError(errMsg(err, "Couldn't load the list."));
    }
  }, [flatId]);

  useEffect(() => {
    load();
  }, [load]);

  const add = (e: React.FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name || !user) return;
    setNewName("");

    const tempId = `temp-${crypto.randomUUID()}`;
    const optimistic: ShoppingItem = {
      id: tempId,
      flatId,
      name,
      addedById: user.id,
      got: false,
      createdAt: new Date().toISOString(),
      addedBy: { id: user.id, name: user.name, colour: user.colour },
    };
    setItems((prev) => (prev ? [optimistic, ...prev] : [optimistic]));

    // Fire-and-forget so the input stays free for rapid entry. Reconcile the
    // temp row with the server row on success, drop it on failure.
    api
      .post<ShoppingItem>(`/shopping/by-flat/${flatId}`, { name })
      .then((item) => {
        setItems((prev) => prev?.map((i) => (i.id === tempId ? item : i)) ?? [item]);
      })
      .catch((err) => {
        setItems((prev) => prev?.filter((i) => i.id !== tempId) ?? null);
        setError(errMsg(err, "Couldn't add the item."));
      });
  };

  const toggleGot = async (item: ShoppingItem) => {
    if (item.id.startsWith("temp-")) return; // not saved yet
    const optimistic = items?.map((i) => (i.id === item.id ? { ...i, got: !i.got } : i)) ?? null;
    setItems(optimistic);
    try {
      await api.patch(`/shopping/${item.id}`, { got: !item.got });
    } catch (err) {
      setError(errMsg(err, "Couldn't update."));
      await load();
    }
  };

  const remove = async (item: ShoppingItem) => {
    if (item.id.startsWith("temp-")) return; // not saved yet
    const previous = items;
    setItems((prev) => prev?.filter((i) => i.id !== item.id) ?? null);
    try {
      await api.delete(`/shopping/${item.id}`);
    } catch (err) {
      setError(errMsg(err, "Couldn't delete."));
      setItems(previous);
    }
  };

  const { pending, got } = useMemo(() => {
    const lists = { pending: [] as ShoppingItem[], got: [] as ShoppingItem[] };
    items?.forEach((i) => (i.got ? lists.got.push(i) : lists.pending.push(i)));
    return lists;
  }, [items]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Shopping</h1>
        <p className="text-sm text-slate-500">Shared list — tick things off as you grab them.</p>
      </div>

      <ErrorBanner message={error} />

      <form onSubmit={add} className="flex gap-2">
        <Input
          placeholder="Add something to the list..."
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          autoFocus
        />
        <Button type="submit" disabled={!newName.trim()}>
          Add
        </Button>
      </form>

      {items === null ? (
        <CenteredSpinner />
      ) : items.length === 0 ? (
        <EmptyState
          title="The list is empty"
          description="Add the first item above."
        />
      ) : (
        <div className="space-y-6">
          <ItemList items={pending} onToggle={toggleGot} onRemove={remove} />
          {got.length > 0 && (
            <details className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <summary className="cursor-pointer text-sm font-medium text-slate-700">
                Got ({got.length})
              </summary>
              <div className="mt-3">
                <ItemList items={got} onToggle={toggleGot} onRemove={remove} />
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function ItemList({
  items,
  onToggle,
  onRemove,
}: {
  items: ShoppingItem[];
  onToggle: (item: ShoppingItem) => void;
  onRemove: (item: ShoppingItem) => void;
}) {
  if (items.length === 0) {
    return <p className="text-sm text-slate-400">Nothing here.</p>;
  }
  return (
    <ul className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <AnimatePresence initial={false}>
      {items.map((item, idx) => (
        <motion.li
          key={item.id}
          layout
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.2 }}
          className={`group flex items-center gap-3 overflow-hidden px-3 py-2.5 sm:px-4 ${
            idx > 0 ? "border-t border-slate-100" : ""
          }`}
        >
          <button
            onClick={() => onToggle(item)}
            aria-label={item.got ? "Mark not got" : "Mark got"}
            className={`grid h-6 w-6 shrink-0 place-items-center rounded-md border-2 transition ${
              item.got
                ? "border-emerald-500 bg-emerald-500 text-white"
                : "border-slate-300 text-transparent hover:border-brand-500"
            }`}
          >
            <LuCheck className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => onToggle(item)}
            className={`flex-1 text-left text-sm ${
              item.got ? "text-slate-400 line-through" : "text-slate-800"
            }`}
          >
            {item.name}
          </button>
          <div className="flex shrink-0 items-center gap-2 text-xs text-slate-400">
            <Avatar
              name={item.addedBy.name}
              colour={item.addedBy.colour}
              size="xs"
              title={`Added by ${item.addedBy.name ?? "?"} ${formatRelativeTime(item.createdAt)}`}
            />
            <IconButton
              icon={<LuTrash2 className="h-4 w-4" />}
              label="Remove item"
              onClick={() => onRemove(item)}
              variant="danger"
            />
          </div>
        </motion.li>
      ))}
      </AnimatePresence>
    </ul>
  );
}
